// frontend/src/components/NextTierUnlock.jsx
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CheckIcon, ArrowUpRightIcon } from "@heroicons/react/20/solid";
import { getUsage } from "../api/orgs";
import { COMPARISON } from "../marketing/data/pricing";
import { planMeta, metric } from "../lib/planMeta";

/** Boolean feature rows in COMPARISON, in display order. */
const FEATURE_LABELS = [
  "API access",
  "Analytics export",
  "Location heatmap",
  "A/B testing (variants)",
  "White-label branding",
];

/** Numeric/limit rows worth showing as a "X → Y" jump. */
const LIMIT_LABELS = [
  "Active QR codes",
  "Scans per month",
  "Team members (seats)",
  "Analytics retention",
];

function row(label) {
  return COMPARISON.find((r) => r.label === label);
}

/**
 * NextTierUnlock — "what you'll unlock" panel for the next tier up. Lists exactly
 * the boolean features the current plan lacks plus the limit increases, all read
 * from `pricing.js` so the figures match the public pricing page
 * (PRIVATE-AREA-PLAN PA5-T2). Renders nothing on the top tier.
 *
 * @param {object}   [props.usage]     Pre-fetched usage payload; skips the fetch.
 * @param {Function} [props.onUpgrade] (planName) => void — wire to checkout.
 * @param {boolean}  [props.isOwner]   Gate the upgrade CTA to billing owners.
 */
function NextTierUnlock({ usage, onUpgrade, isOwner }) {
  const [plan, setPlan] = useState(usage?.plan || null);

  useEffect(() => {
    if (usage?.plan) {
      setPlan(usage.plan);
      return undefined;
    }
    let active = true;
    getUsage()
      .then((d) => active && setPlan(d.plan))
      .catch(() => active && setPlan(null));
    return () => {
      active = false;
    };
  }, [usage]);

  if (!plan?.name) return null;
  const { id, next } = planMeta(plan.name);
  if (!next) return null; // already on the top tier

  const newFeatures = FEATURE_LABELS.filter((label) => {
    const r = row(label);
    return r && r[id] !== true && r[next.id] === true;
  });

  const limitJumps = LIMIT_LABELS.map((label) => ({
    label,
    from: metric(id, label),
    to: metric(next.id, label),
  })).filter((j) => j.from != null && j.to != null && j.from !== j.to);

  return (
    <section
      aria-labelledby="next-tier-heading"
      className="rounded-xl border border-brand-200 bg-brand-50/60 p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="next-tier-heading" className="text-base font-semibold text-gray-900">
          Unlock more with {next.name}
        </h2>
        {onUpgrade && (
          <button
            type="button"
            onClick={() => onUpgrade(next.name)}
            disabled={!isOwner}
            title={isOwner ? undefined : "Only an owner can change the plan"}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
          >
            Upgrade to {next.name}
            <ArrowUpRightIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {limitJumps.length > 0 && (
        <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {limitJumps.map((j) => (
            <div key={j.label} className="rounded-lg bg-white p-3">
              <dt className="text-xs text-gray-500">{j.label}</dt>
              <dd className="mt-0.5 text-sm font-semibold text-gray-900">
                {j.from} <span className="text-brand-600">→ {j.to}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {newFeatures.length > 0 && (
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {newFeatures.map((f) => (
            <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
              <CheckIcon className="h-4 w-4 shrink-0 text-accent-600" />
              {f}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

NextTierUnlock.propTypes = {
  usage: PropTypes.object,
  onUpgrade: PropTypes.func,
  isOwner: PropTypes.bool,
};
NextTierUnlock.defaultProps = { usage: null, onUpgrade: null, isOwner: false };

export default NextTierUnlock;
